/**
 * IconLink component for the Indigo design system.
 *
 * An inline link led by a glyph — "Open document", "Open ticket". It is an
 * `<a>`, not a button: it takes the person somewhere (a record, a file, an
 * external system). For an in-place action use `Button variant="link"`.
 *
 * Body S (Medium) in the link colour; the glyph follows the text colour, so a
 * hover tints both. `external` opens the target in a new tab.
 *
 * @example
 * ```tsx
 * <IconLink href={ticket.url} external>Open ticket</IconLink>
 * <IconLink href={fileUrl} iconName="file">Download</IconLink>
 * ```
 */
import { clsx } from "clsx";
import { type AnchorHTMLAttributes, forwardRef, type ReactNode } from "react";
import { Icon, type MonoIconName } from "../Icons";
import "./IconLink.css";

export interface IconLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children"> {
  /** Link destination. */
  href: string;

  /** Link text. */
  children: ReactNode;

  /**
   * Leading glyph. A mono icon, since it takes the link colour.
   * @default "file-search"
   */
  iconName?: MonoIconName;

  /** Opens the link in a new tab (with `rel="noopener noreferrer"`). */
  external?: boolean;

  /** Test id applied to the link. */
  "data-testid"?: string;
}

export const IconLink = forwardRef<HTMLAnchorElement, IconLinkProps>(
  (
    {
      href,
      children,
      iconName = "file-search",
      external = false,
      className,
      target,
      rel,
      ...rest
    },
    ref,
  ) => (
    <a
      ref={ref}
      href={href}
      className={clsx("indigo-icon-link", className)}
      target={external ? "_blank" : target}
      rel={external ? "noopener noreferrer" : rel}
      {...rest}
    >
      <Icon name={iconName} size="s" className="indigo-icon-link__icon" />
      <span className="indigo-icon-link__label">{children}</span>
    </a>
  ),
);

IconLink.displayName = "IconLink";
